/**
 * What the register wizard shows and accepts: the four stages, the form's
 * values, which collections may receive a work, and which dropped files may go
 * in.
 *
 * A collector uses it to put a work on the registry, and the question every
 * step answers is "may this go forward?" — the validation is here, in one
 * entry point, so `useRegisterController.ts` carries no switch of its own. The
 * chain-of-title rules are not here either; they are `provenance.model.ts`'s
 * and this file calls them. The words and limits sit in
 * `register.constants.ts`.
 *
 * Images are screened on drop and held in the browser until submission,
 * because a work — the thing an image attaches to — is minted at the end of
 * the wizard and not the start.
 */

import { canCurate } from "@ondo/shared";
import type {
  ArtistMatch,
  ArtistSummary,
  CollectionSummary,
  ImageKind,
  RegisterWorkRequest,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { IMAGE_KIND_LABELS } from "../lib/constants.ts";
import { describeBytes, fileKey } from "../lib/files.ts";
import { validateEntries } from "../provenance/provenance.model.ts";
import type { ProvenanceItem } from "../provenance/provenance.model.ts";
import {
  ACCEPTED_IMAGE_TYPES,
  CORE_VIEWS,
  DECLARATIONS,
  MAX_IMAGES,
  MAX_IMAGE_BYTES,
  REGISTER_COPY,
  REGISTER_STEPS,
} from "./register.constants.ts";
import type { DeclarationKey } from "./register.constants.ts";

/**
 * Which panel the wizard is showing.
 *
 * A stage union rather than a route, exactly as `useSignupController` does it:
 * `…/register/provenance` on a cold load would be a Provenance panel over an
 * empty form model, which is not a state worth being able to link to.
 */
export type Stage =
  | { kind: "images" }
  | { kind: "details" }
  /**
   * OND-90. "Did you mean one of these?", shown on the way out of the details
   * step when a name was typed and no artist was picked.
   *
   * A stage of its own rather than a panel flag, so the wizard cannot be on
   * the details step and awaiting a confirmation at the same time — and it
   * carries the NAME IT IS ABOUT, which is the thing `BuyerCheck` in the sell
   * wizard exists to demonstrate: "a stale 'Sarah Okonkwo' under a
   * half-retyped id is the worst state this step can be in". Here the
   * equivalent is a list of candidates for "Erwin Wurm" shown over a field now
   * reading "Erwin Wurmann", and the cure is the same — the question travels
   * with its subject.
   */
  | { kind: "artist-match"; typedName: string; candidates: ArtistMatch[] }
  | { kind: "provenance" }
  | { kind: "confirm" }
  /** Carries the result, so it cannot be read before the API has answered. */
  | {
      kind: "registered";
      /** The work that now exists. Carried so it can be linked to. */
      workId: string;
      title: string;
      /**
       * What went through with something refused.
       *
       * An ONDO id that does not exist is only answerable at submit — there is
       * no directory to check it against first — so a queued ask can fail while
       * the work itself is created. Carried on the stage rather than in `error`,
       * because the work exists.
       */
      notices: string[];
    };

export interface ArtworkValues {
  /**
   * Which collection the work is registered INTO.
   *
   * This is the ownership statement, and it is the reason there is no owner
   * name on this form. Who owns a work is which collection holds it — a
   * `collection_grant` — so a typed owner would be a second, unenforced
   * ownership channel rendering next to registry-verified fields.
   */
  collectionId: string | null;
  title: string;
  attributedName: string;
  /** Null when the artist is not in the catalogue. The submission then routes to ONDO. */
  artistId: string | null;
  /**
   * OND-90. The registrant has been shown the candidates for this exact string
   * and said none of them is the artist.
   *
   * THE STRING AND NOT A BOOLEAN, for `confirmedOndoId`'s reason in the sell
   * wizard: "An id rather than a boolean, so that retyping the field cannot
   * leave a confirmation standing over a different buyer." Retyping the artist
   * field moves `attributedName` away from this value, the decline stops
   * applying, and the step asks again — which is what must happen, because the
   * decline was about a different name.
   */
  artistDeclinedFor: string | null;
  year: string;
  medium: string;
  dimensions: string;
  dateConceived: string;
  dateExecuted: string;
  editionText: string;
  signed: boolean;
  description: string;
  /**
   * Still one line per entry, and still free text.
   *
   * Provenance moved to ondo.provenance_entries and is no longer a field on
   * this form — see src/provenance/. Exhibitions did not: there is no
   * structured table behind them yet, and inventing one here would be a shape
   * the registry could not store.
   */
  exhibitions: string;
  declarations: Record<DeclarationKey, boolean>;
}

export const EMPTY_ARTWORK: ArtworkValues = {
  collectionId: null,
  title: "",
  attributedName: "",
  artistId: null,
  artistDeclinedFor: null,
  year: "",
  medium: "",
  dimensions: "",
  dateConceived: "",
  dateExecuted: "",
  editionText: "",
  signed: false,
  description: "",
  exhibitions: "",
  declarations: { true: false, right: false, authority: false, advice: false },
};

export interface TargetOption {
  id: string;
  label: string;
}

/**
 * An image chosen on this device and not yet uploaded.
 *
 * The bytes stay in the browser until submission, because there is nothing to
 * attach them to before then: `ondo.work_images.object_path` is generated from a
 * work id, and the work is not created until the collector commits. An abandoned
 * wizard therefore leaves no rows and no orphaned objects in the bucket.
 *
 * The reason changed shape in 20260804090700 and the conclusion did not. It used
 * to be a draft registration the images hung off; it is now the work itself. In
 * both cases the parent is minted at the end of the wizard, not the start.
 */
export interface PendingImage {
  /** Client-side only. The row's real id is minted by the database on insert. */
  id: string;
  file: File;
  kind: ImageKind;
  /** An object URL for the thumbnail. Revoked when the image is dropped. */
  previewUrl: string;
}

/** The outcome of dropping or picking files: what may be added, and what may not. */
export interface ImageIntake {
  accepted: File[];
  /** One sentence per refusal, in the order the files arrived. */
  rejected: string[];
}

/** A view the submission is expected to carry, and whether it has one. */
export interface ViewRow {
  kind: ImageKind;
  label: string;
  satisfied: boolean;
  /** False for the two core views, which cannot be removed from the list. */
  removable: boolean;
}

export interface SummaryRow {
  label: string;
  value: string;
}

export interface DeclarationRow {
  key: DeclarationKey;
  label: string;
  checked: boolean;
}

/** Zero-based, for the step markers. Mirrors signup's stepIndex. */
export function stepIndex(stage: Stage): number {
  switch (stage.kind) {
    case "images":
      return 0;
    // The artist confirmation belongs to the details step rather than being a
    // fifth marker. It is a question about one field on that step, asked on the
    // way out — numbering it would tell every registrant the wizard is five
    // steps long when four of them never see it.
    case "details":
    case "artist-match":
      return 1;
    case "provenance":
      return 2;
    case "confirm":
      return 3;
    default:
      // Registered sits past the last marker, so nothing reads as incomplete.
      return REGISTER_STEPS.length;
  }
}

/**
 * Where a work may be registered.
 *
 * `canCurate` is the same predicate the policies use, so a destination offered
 * here is one `ondo.begin_registration()` will accept. A collection the caller
 * can merely see is not a target, however visible it is.
 */
export function registerTargets(collections: CollectionSummary[]): TargetOption[] {
  return collections
    .filter((collection) => canCurate(collection.level))
    .map((collection) => ({ id: collection.id, label: collection.name }));
}

/**
 * Which of the dropped files may be added, and why the rest may not.
 *
 * Every refusal is checked here rather than at upload time, so a file the
 * bucket's constraints would reject is refused while the collector is still
 * looking at the dropzone — not three steps later when the submission fails.
 * Takes plain `File`s so it stays testable: nothing in this function touches
 * the DOM or creates an object URL.
 */
export function screenImages(
  incoming: File[],
  existing: PendingImage[],
): ImageIntake {
  const accepted: File[] = [];
  const rejected: string[] = [];
  const seen = new Set(existing.map((image) => fileKey(image.file)));

  for (const file of incoming) {
    if (!isAcceptedType(file.type)) {
      rejected.push(`${file.name} is not a JPEG, PNG, TIFF, HEIC or WebP.`);
      continue;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      rejected.push(`${file.name} is ${describeBytes(file.size)} — the limit is 50MB.`);
      continue;
    }
    if (file.size === 0) {
      rejected.push(`${file.name} is empty.`);
      continue;
    }
    if (seen.has(fileKey(file))) {
      rejected.push(`${file.name} has already been added.`);
      continue;
    }
    if (existing.length + accepted.length >= MAX_IMAGES) {
      rejected.push(`${file.name} was not added — ${String(MAX_IMAGES)} images is the most a submission can carry.`);
      continue;
    }

    seen.add(fileKey(file));
    accepted.push(file);
  }

  return { accepted, rejected };
}

/**
 * What an incoming image is presumed to be.
 *
 * Front, then back, then unlabelled: the order all but one collector uploads
 * in, and a guess they can correct on the thumbnail. Presuming 'other' for
 * everything would be more honest and would mean every submission arrives with
 * the two views the authority actually needs left unnamed.
 */
export function nextKind(existing: PendingImage[]): ImageKind {
  const taken = new Set(existing.map((image) => image.kind));
  if (!taken.has("front")) return "front";
  if (!taken.has("back")) return "back";
  return "other";
}

/**
 * The views this submission is expected to carry.
 *
 * Signature is on the list exactly when the collector says the work is signed —
 * the one required view that follows from an answer rather than from a choice,
 * which is why that checkbox sits on this step and not with the details.
 */
export function viewRows(input: {
  signed: boolean;
  extras: ImageKind[];
  images: PendingImage[];
}): ViewRow[] {
  const kinds: ImageKind[] = [
    ...CORE_VIEWS,
    ...(input.signed ? (["signature"] as const) : []),
    ...input.extras.filter((kind) => !isCoreView(kind) && kind !== "signature"),
  ];

  return kinds.map((kind) => ({
    kind,
    label: IMAGE_KIND_LABELS[kind],
    satisfied: input.images.some((image) => image.kind === kind),
    removable: !isCoreView(kind) && kind !== "signature",
  }));
}

/** One entry point, so the controller carries no switch of its own. */
export function validateStep(
  stage: Stage["kind"],
  values: ArtworkValues,
  provenance: ProvenanceItem[],
): string | null {
  switch (stage) {
    case "images":
      // Nothing is required here yet. Images are evidence for the authority
      // rather than a condition of submitting.
      return null;
    case "details":
      return validateArtwork(values);
    case "provenance":
      // Optional as a whole, and strict about the one field an entry cannot do
      // without: a link naming nobody is not a link.
      return validateEntries(provenance);
    case "confirm":
      return validateDeclarations(values);
    default:
      return null;
  }
}

export function validateArtwork(values: ArtworkValues): string | null {
  if (!values.collectionId) return REGISTER_COPY.targetRequired;
  if (values.title.trim() === "") return REGISTER_COPY.titleRequired;
  if (values.attributedName.trim() === "") return REGISTER_COPY.artistRequired;
  return null;
}

export function validateDeclarations(values: ArtworkValues): string | null {
  const all = DECLARATIONS.every((declaration) => values.declarations[declaration.key]);
  return all ? null : REGISTER_COPY.declarationsRequired;
}

/**
 * Trims, and turns an empty optional into null rather than "".
 *
 * `artistId` goes as it stands, including null after a decline — and that is
 * SAFE rather than a gap, because `ondo.place_new_work()` resolves a null
 * artist by the same rule this form prompted with. A decline can only have
 * happened where there was not exactly one exact match, which is exactly the
 * case the constructor also refuses to resolve. One rule, asked twice, which is
 * why `exact` is computed in SQL rather than here.
 */
export function toRegisterRequest(values: ArtworkValues): RegisterWorkRequest {
  return {
    title: values.title.trim(),
    attributedName: values.attributedName.trim(),
    artistId: values.artistId,
    year: blankToNull(values.year),
    medium: blankToNull(values.medium),
    dimensions: blankToNull(values.dimensions),
    dateConceived: blankToNull(values.dateConceived),
    dateExecuted: blankToNull(values.dateExecuted),
    editionText: blankToNull(values.editionText),
    signed: values.signed,
    description: blankToNull(values.description),
  };
}

/** Free-text lines, one claim each, with the blanks dropped. */
export function toEntries(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function confirmRows(
  values: ArtworkValues,
  targetName: string,
  provenance: string,
): SummaryRow[] {
  return [
    { label: "Title", value: values.title.trim() || "—" },
    // OND-90. The typed name AND whether anything is behind it.
    //
    // This row said only the string before, which is the half of the bug that
    // happened on screen: "the collector's own screen has said 'Erwin Wurm' the
    // whole time and gives no hint the link failed". The last screen before
    // submitting is where the difference has to be legible, because after it
    // there is no way back inside the product.
    {
      label: "Artist",
      value: artistRow(values),
    },
    { label: "Year", value: values.year.trim() || "—" },
    { label: "Medium", value: values.medium.trim() || "—" },
    { label: "Dimensions", value: values.dimensions.trim() || "—" },
    // Not "Owner". The collection is the ownership record.
    { label: "Registered into", value: targetName || "—" },
    // Structured rows now, and the count names their documents: "3 entries,
    // none documented" and "3 entries, all documented" are materially different
    // submissions, and this is the last screen before it goes.
    { label: "Provenance", value: provenance },
    { label: "Exhibitions", value: countLines(values.exhibitions, "entry", "entries") },
    { label: "Reference", value: REGISTER_COPY.ondoIdNote },
  ];
}

export function declarationRows(values: ArtworkValues): DeclarationRow[] {
  return DECLARATIONS.map((declaration) => ({
    key: declaration.key,
    label: declaration.label,
    checked: values.declarations[declaration.key],
  }));
}

/**
 * What to do when the registrant tries to leave the details step.
 *
 * OND-90, and the whole of its logic. Four outcomes, and the one that matters
 * is `confirm`:
 *
 *   `already-picked`  they chose from the suggestions. Nothing to ask.
 *   `linked`          exactly one candidate IS the name they typed. The link
 *                     is attached and they are not interrupted — "an exact
 *                     typed name links without a prompt".
 *   `confirm`         anything else with candidates: a near miss, or two
 *                     artists sharing the name. Ask, and link NOTHING in the
 *                     meantime. The silent guess is the bug.
 *   `not-on-ondo`     no candidates at all. Say so plainly, and register the
 *                     work anyway.
 *
 * `declinedFor` is the name they already said no to. When it still matches
 * what is in the field, they are not asked twice — and when they have since
 * retyped, the decline no longer applies and they are.
 *
 * Pure, and the exact-match test is NOT done here: `exact` is decided by
 * `ondo.match_artists()` so that `ondo.place_new_work()` can answer the same
 * question at insert from the same rule. A `===` on `displayName` in this file
 * would have been a second spelling of "is this the same name", free to
 * disagree with the one the database links by — and the disagreement would be
 * invisible, because the form would prompt and the server would link.
 */
export type ArtistGate =
  | { kind: "already-picked" }
  | { kind: "linked"; artist: ArtistMatch }
  | { kind: "confirm"; typedName: string; candidates: ArtistMatch[] }
  | { kind: "not-on-ondo"; typedName: string };

export function artistGate(input: {
  typedName: string;
  artistId: string | null;
  declinedFor: string | null;
  candidates: ArtistMatch[];
}): ArtistGate {
  if (input.artistId !== null) return { kind: "already-picked" };

  const typedName = input.typedName.trim();
  // Compared trimmed on both sides, because the field is trimmed before it is
  // sent and a trailing space is not a change of mind.
  if (input.declinedFor !== null && input.declinedFor.trim() === typedName) {
    return { kind: "already-picked" };
  }

  const exact = input.candidates.filter((candidate) => candidate.exact);
  // EXACTLY one. Two artists may share a display name — `ondo.artists` permits
  // it when the birth years differ — and choosing between them unasked is the
  // silent misattribution this whole ticket is the mirror image of.
  if (exact.length === 1 && exact[0]) return { kind: "linked", artist: exact[0] };

  if (input.candidates.length === 0) return { kind: "not-on-ondo", typedName };
  return { kind: "confirm", typedName, candidates: input.candidates };
}

/** How a candidate reads in the list: 'Erwin Wurm, Austrian, 1954–'. */
export function describeCandidate(candidate: ArtistMatch): string {
  const dates =
    candidate.birthYear === null
      ? null
      : `${String(candidate.birthYear)}–${candidate.deathYear === null ? "" : String(candidate.deathYear)}`;
  // Nationality and dates are what tell two artists of the same name apart,
  // which is the case the confirmation step exists to put a question to. No
  // authority is named, here or anywhere on this screen.
  return [candidate.displayName, candidate.nationality, dates]
    .filter((part): part is string => part !== null && part !== "")
    .join(", ");
}

/**
 * How an artist choice reads once made.
 *
 * The copy never names the authority, because the API never returns one. That
 * omission is the security boundary: naming it would turn this form into a
 * walkable directory of every estate ONDO has signed.
 */
export function describeRouting(artist: ArtistSummary | null): string {
  if (!artist) return REGISTER_COPY.noAuthority;
  return artist.hasAuthority ? REGISTER_COPY.hasAuthority : REGISTER_COPY.noAuthority;
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message.trim() === "" ? REGISTER_COPY.unknownError : error.message;
  }
  return REGISTER_COPY.unknownError;
}

/** The typed name with the one fact about it that is not visible in the string. */
function artistRow(values: ArtworkValues): string {
  const typed = values.attributedName.trim();
  if (typed === "") return "—";
  const tag =
    values.artistId === null
      ? REGISTER_COPY.artistUnlinkedTag
      : REGISTER_COPY.artistLinkedTag;
  return `${typed} — ${tag}`;
}

function isAcceptedType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);
}

function isCoreView(kind: ImageKind): boolean {
  return (CORE_VIEWS as readonly ImageKind[]).includes(kind);
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function countLines(value: string, one: string, many: string): string {
  const count = toEntries(value).length;
  if (count === 0) return "None recorded";
  return count === 1 ? `1 ${one}` : `${count} ${many}`;
}
